import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pxToRem } from 'assets/styles/convertors';
import { CalendarRound, PromotionAuthor } from 'assets/svgs/16px';
import Accordion from 'components/Accordion/Accordion';
import BaseButton from 'components/Buttons/BaseButton';
import { Relative } from 'components/commonStyles';
import Container from 'components/Container/Container';
import PageHeader from 'components/PageHeader/PageHeader';
import { RichText } from 'components/RichText';
import { H1 } from 'components/Text/styles';
import UI from 'constants/ui';
import { Promotion } from 'generated/contentful-graphql';
import { useDateFormat } from 'hooks/format/useDateFormat';
import Link from 'next/link';

import { Flex } from '../../../components/Flex';
import { PromotionsCardImage } from '../styles';

import {
  PromotionInfoDetails,
  PromotionsInfoBody,
  PromotionsInfoDateWrapper,
  PromotionsInfoDivider,
  PromotionsInfoElement,
  PromotionsInfoTag,
} from './styles';

interface PromotionArticleProps extends Promotion {
  preview?: boolean;
}

const PromotionArticle = ({ preview = false, ...props }: PromotionArticleProps) => {
  const { t } = useTranslation();

  const { title, image, tags, author, startsAt, button, content, termsAndConditions, type } =
    preview ? getFallbackPreviewProps(props) : props;

  const [openTerms, setOpenTerms] = useState(true);

  return (
    <article>
      <PageHeader
        pageName={title ?? ''}
        goBack={type === 'casino' ? '/' : '/sports'}
        truncateText
      />
      <Container padding={{ desktop: `0 ${UI.spacing.xl}` }}>
        <Flex wide gap={UI.spacing.md} justify="center">
          <PromotionsInfoElement>
            <Relative style={{ aspectRatio: (image?.width ?? 16) / (image?.height ?? 9) }}>
              <PromotionsCardImage
                layout="fill"
                alt={image?.description ?? ''}
                src={image?.url ?? ''}
              />
            </Relative>

            <PromotionsInfoBody>
              <div>
                <H1>{title}</H1>
              </div>
              <PromotionInfoDetails>
                <Flex gap="1rem">
                  {tags?.map((t, i) => (
                    <Link href="/" passHref key={i}>
                      <a>
                        <PromotionsInfoTag>{t}</PromotionsInfoTag>
                      </a>
                    </Link>
                  ))}
                </Flex>
                <Flex gap={pxToRem(12)} align="center">
                  {author && (
                    <Flex gap={pxToRem(6)} align="center">
                      <PromotionAuthor />
                      {author.name}
                    </Flex>
                  )}
                  <PromotionsInfoDivider />
                  <PromotionsInfoDate date={startsAt} />
                </Flex>
              </PromotionInfoDetails>

              <RichText content={content?.json} />

              {button && (
                <Link href={button.href ?? '/'} passHref>
                  <a>
                    <BaseButton value={t(button.value || '')} />
                  </a>
                </Link>
              )}

              {termsAndConditions && (
                <Accordion
                  open={openTerms}
                  toggleOpen={() => setOpenTerms(!openTerms)}
                  header={<strong>Terms and conditions</strong>}
                >
                  <RichText content={termsAndConditions.json} />
                </Accordion>
              )}
            </PromotionsInfoBody>
          </PromotionsInfoElement>
        </Flex>
      </Container>
    </article>
  );
};

/** Gets the props for a preview page; filling in blank data where needed. */
const getFallbackPreviewProps = (promotion: Partial<Promotion>): Promotion => {
  const props = {
    ...promotion,
    title: promotion.title ?? 'Lorem Ispum',
    image: promotion.image ?? {
      url: '',
      title: '',
      description: '',
    },
    button: promotion.button ?? null,
    author: promotion.author ?? { name: 'Shuffle Team' },
    startsAt: new Date('2000-01-01'),
    content: promotion.content ?? null,
    tags: promotion.tags ?? ['casino'],
    type: promotion.type ?? 'casino',
  } as Promotion;

  return props;
};

interface PromotionsInfoDateProps {
  prefix?: string;
  date?: Date;
  color?: string;
}

const PromotionsInfoDate = ({ prefix, date, color }: PromotionsInfoDateProps) => {
  const { formatDate } = useDateFormat();
  const { t } = useTranslation();

  return (
    <PromotionsInfoDateWrapper $color={color}>
      <CalendarRound color={color} />
      {date && prefix && `${prefix} `}
      {date ? formatDate(date, 'LL') : t('txtNoEndDate')}
    </PromotionsInfoDateWrapper>
  );
};

export default PromotionArticle;
